import React, {Component, useRef} from "react";
import moment from "moment";
import {Link} from "react-router-dom";
import _ from "lodash";

import DateTimeRangePicker from "components/DateTimeRangePicker";
import Api from "util/Api";
import AlertCard from "../../../components/neighborhood-data/AlertCard";
import MovementCard from "../../../components/neighborhood-data/MovementCard";
import OutOfRangeCard from "../../../components/neighborhood-data/OutOfRangeCard";
import SleepCard from "../../../components/neighborhood-data/SleepCard";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {BrowserDetection, openTab} from "../../../util/Browser";

const MIN_TIMESTAMP = -864e13;
let rooms = [];
let alerts = {};
let movements = {};
let outOfRangeCount = {};

const PIXELS_TO_POINTS = 0.75;
const smallFontSize = 14;
const mediumFontSize = 18;
const largeFontSize = 22;

class SectionData extends Component {
    constructor(props) {
        super(props);

        this.dateTimePickerDefault = {
            startTime: "00:00",
            endTime: "23:59",
            startDate: moment().subtract(24, "h"),
            endDate: moment(),
            lowerLimit: MIN_TIMESTAMP,
            upperLimit: moment()
        };

        this.state = {
            selectedTimeFrame: "Last 24 Hours",
            startTime: moment().subtract(24, "hours"),
            endTime: moment(),
            timeFrameDropdownVisible: false,
            customRangePickerVisible: false,
            isUsingCustomDateRange: false,
            lowerLimit: this.dateTimePickerDefault.lowerLimit,
            upperLimit: this.dateTimePickerDefault.upperLimit,
            isLoadingAlerts: true,
            isLoadingMovement: true,
            isLoadingOutOfRange: true,
            isLoadingSleep: true,
            isPrintable: false,
            rooms: []
        };
        this.printDataReport = this.printDataReport.bind(this);
        this.handleTimeFrameChange = this.handleTimeFrameChange.bind(this);
        this.checkIsReadyToPrint = this.checkIsReadyToPrint.bind(this);
        this.neighborhoodDataContainer = React.createRef();
    }
    componentDidMount() {
        const {facility, section} = this.props.match.params;
        this.setState((state) => ({
            ...state,
            facilityThresholds: {},
            section,
            facility
        }));
        this.loadBedData().then(() => {
            this.refreshData();
        });
    }

    handleTimeFrameChange(event) {
        const startTime = moment(Number(event.target.value));
        const selectedTimeFrame = event.target.innerHTML;

        this.setState(
            (state) => ({
                startTime,
                selectedTimeFrame,
                timeFrameDropdownVisible: false
            }),
            this.refreshData
        );
    }

    loadBedData() {
        rooms = [];
        return Api.withAuth(this.props.auth)
            .getBeds({
                ...this.props.match.params
            })
            .then((data) => {
                data.forEach((bed) => {
                    if (bed.bed_id && bed.patient_id) {
                        rooms.push(bed);
                    }
                });
            });
    }

    refreshData() {
        this.getAlerts();
        this.getMovement();
        this.getSleep();
        this.getOutOfRange();
    }
    reportHeaderHeight() {
        const blankSpace = 50;
        return (
          (largeFontSize + smallFontSize + mediumFontSize + blankSpace) *
          PIXELS_TO_POINTS
        );
    }
    drawHeader(pdf, width, padding) {
        const {
            facilityDetails: { facilityName },
            sectionDetails: { sectionName = 'Section Name' },
        } = this.props.auth;
        const { name: roomName = 'Default room', startTime, endTime } = this.state;

        const mediumMargin = 25 * PIXELS_TO_POINTS;
        const smallMargin = 10 * PIXELS_TO_POINTS;
        const datetimeFormat = 'hh:mmA  MMM DD, YYYY';
        const startTimeText = startTime.format(datetimeFormat);
        const endTimeText = (endTime || moment()).format(datetimeFormat);

        const communityLength =
          pdf.getStringUnitWidth(facilityName) * mediumFontSize;
        const neighborhoodLength =
          pdf.getStringUnitWidth(sectionName) * mediumFontSize;
        const startTimeLength =
          pdf.getStringUnitWidth(startTimeText) * smallFontSize;
        const endTimeLength = pdf.getStringUnitWidth(endTimeText) * smallFontSize;

        const communityYPos = padding;
        const sectionXPos = width - padding - neighborhoodLength;

        const roomLabelYPos = communityYPos + 2 * mediumMargin;
        const roomTitleYPos = roomLabelYPos + mediumMargin;

        const startTimeLabelYPos =
          roomTitleYPos + largeFontSize - 2 * smallFontSize;
        const startTimeXPos =
          width - padding - startTimeLength - endTimeLength - mediumMargin;
        const startTimeTitleYPos = startTimeLabelYPos + smallFontSize;

        pdf.setFontType('normal');
        pdf.setFontSize(mediumFontSize);
        pdf.text(
          facilityName,
          sectionXPos - communityLength - smallMargin,
          communityYPos
        );

        pdf.setFontSize(smallFontSize);
        pdf.text('Personal Data For', padding, roomLabelYPos);
        pdf.text('From', startTimeXPos, startTimeLabelYPos);
        pdf.text(
          'To',
          startTimeXPos + startTimeLength + mediumMargin,
          startTimeLabelYPos
        );

        pdf.setFontType('bold');
        pdf.text(startTimeText, startTimeXPos, startTimeTitleYPos);
        pdf.text(
          endTimeText,
          startTimeXPos + startTimeLength + mediumMargin,
          startTimeTitleYPos
        );

        pdf.setFontSize(mediumFontSize);
        pdf.text(sectionName, sectionXPos, communityYPos);

        pdf.setFontSize(largeFontSize);
        pdf.text(roomName, padding, roomTitleYPos);
    }
    printDataReport() {
        html2canvas(this.neighborhoodDataContainer.current).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const padding = 50;

            const width = (canvas.width + 2 * padding) * PIXELS_TO_POINTS;
            const height = Math.max(
              (canvas.height + 2 * padding + this.reportHeaderHeight()) *
              PIXELS_TO_POINTS,
              width / 1.4142
            );
            const pdf = new jsPDF('l', 'pt', [height, width]);
            pdf.addImage(
              imgData,
              'PNG',
              padding,
              padding + this.reportHeaderHeight()
            );
            this.drawHeader(pdf, width, padding * PIXELS_TO_POINTS);

            pdf.autoPrint();

            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(pdf.output('blob'));
            } else if (BrowserDetection.is_safari) {
                window.open(pdf.output('bloburl'));
            } else {
                openTab(pdf.output('bloburl'));
            }
        });
    }
    getAlerts(){
        const {facilityDetails, sectionDetails} = this.props.auth;
        const facility = facilityDetails.facilityName;
        const section = sectionDetails.sectionName;
        const startTime = this.state.startTime.valueOf();

        this.setState({isLoadingAlerts: true});
        Api.withAuth(this.props.auth)
            .getNeighborhoodSummaryAlerts({facility, section, startTime})
            .then((alertResponse) => {
                alerts = alertResponse;
                alertResponse.details.forEach((alert) => {
                    const bedToModify = rooms.find((bed) => bed.patient_id === alert.patient_id);
                    if (bedToModify) {
                        bedToModify.active_alerts = alert;
                    }
                });
                this.setState({isLoadingAlerts: false});
                this.checkIsReadyToPrint();
            });
    }
    getMovement(){
        const {facilityDetails, sectionDetails} = this.props.auth;
        const facility = facilityDetails.facilityName;
        const section = sectionDetails.sectionName;

        this.setState({isLoadingMovement: true});
        Api.withAuth(this.props.auth)
            .getNeighborhoodSummaryMovement({facility, section})
            .then((movementResponse) => {
                movements = movementResponse;
                this.setState({isLoadingMovement: false});
                this.checkIsReadyToPrint();
            });
    }
    getOutOfRange(){
        const {facilityDetails, sectionDetails} = this.props.auth;
        const facility = facilityDetails.facilityName;
        const section = sectionDetails.sectionName;
        const startTime = this.state.startTime.valueOf();
        this.setState({isLoadingOutOfRange: true});
        Api.withAuth(this.props.auth)
            .getNeighborhoodSummaryOutOfRange({facility, section, startTime})
            .then((outOfRangeResponse) => {
                outOfRangeCount = outOfRangeResponse;
                this.setState({isLoadingOutOfRange: false});
                this.checkIsReadyToPrint();
            });
    }
    getSleep(){
        const {facilityDetails, sectionDetails} = this.props.auth;
        const facility = facilityDetails.facilityName;
        const section = sectionDetails.sectionName;
        const startTime = this.state.startTime.valueOf();

        this.setState({isLoadingSleep: true});
        Api.withAuth(this.props.auth)
            .getNeighborhoodSummarySleep({facility, section, startTime})
            .then((sleepResponse) => {
                sleepResponse.forEach((sleepRow) => {
                    const roomToModify = rooms.find((room) => room.patient_id === sleepRow.patientId);
                    if (roomToModify) {
                        roomToModify.sleep = sleepRow;
                    }
                });
                this.setState({isLoadingSleep: false});
                this.checkIsReadyToPrint();
            });
    }

    checkIsReadyToPrint() {
        const loadingStates = [this.state.isLoadingAlerts, this.state.isLoadingMovement, this.state.isLoadingSleep, this.state.isLoadingOutOfRange];
        const isPrintable = loadingStates.every((loadingState) => loadingState !== true);
        !isPrintable || this.setState({isPrintable: isPrintable});
    }

    render() {
        const {
            lowerLimit,
            upperLimit,
            customRangePickerVisible
        } = this.state;

        const timeFrameOptions = [
            {
                text: "Last 24 Hours",
                startTime: moment().subtract(24, "hours")
            },
            {
                text: "Last 7 Days",
                startTime: moment().subtract(7, "days")
            }
        ];

        const timeFrame = moment.duration(moment().diff(this.state.startTime));
        let timeFrameText;

        if (timeFrame < 25 * 60 * 60 * 1000) {
            timeFrameText = `${Math.floor(
                moment.duration(timeFrame).asHours()
            )} hours`;
        } else {
            timeFrameText = `${Math.round(moment.duration(timeFrame).asDays())} days`;
        }
        return (
            <>
                <div className="section-data">
                    <div className="subnav fixed-top">
                        <div className="container h-100">
                            <div className="row align-items-center h-100">
                                <div className="mr-auto d-flex align-items-center">
                                    <Link to={`./rooms`}>
                                        <h3>{_.get(this.state, "section")}</h3>
                                    </Link>
                                    <span className="separator"/>
                                    <h3>Neighborhood Data</h3>
                                    <div style={{fontSize: 12, marginLeft: 12}}> Click on any Resident Name to navigate to their patient page</div>
                                </div>

                                <div className="d-flex">
                                    <div className="time-frame d-flex align-items-stretch">
                                        <div
                                            className={
                                                this.state.timeFrameDropdownVisible
                                                    ? "dropdown show"
                                                    : "dropdown"
                                            }
                                        >
                                            <button
                                                className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                onClick={() =>
                                                    this.setState({
                                                        timeFrameDropdownVisible: !this.state
                                                            .timeFrameDropdownVisible,
                                                        customRangePickerVisible: false
                                                    })
                                                }
                                            >
                                                {this.state.selectedTimeFrame}
                                            </button>
                                            <div
                                                className={`dropdown-menu time-period-dropdown ${
                                                    this.state.timeFrameDropdownVisible ? "show" : ""
                                                }`}
                                                style={
                                                    customRangePickerVisible ? {minWidth: 700} : {}
                                                }
                                            >
                                                {customRangePickerVisible && (
                                                    <div
                                                        className="time-period-side-content"
                                                        style={
                                                            customRangePickerVisible ? {width: 500} : {}
                                                        }
                                                    >
                                                        <DateTimeRangePicker
                                                            startDateId="startDate"
                                                            endDateId="endDate"
                                                            defaultStartDate={
                                                                this.dateTimePickerDefault.startDate
                                                            }
                                                            defaultEndDate={
                                                                this.dateTimePickerDefault.endDate
                                                            }
                                                            onDatesChange={this.handleDatesChange}
                                                            isOutsideRange={(date) =>
                                                                !date.isBetween(
                                                                    lowerLimit,
                                                                    upperLimit,
                                                                    "day",
                                                                    "[]"
                                                                )
                                                            }
                                                            defaultStartTime={
                                                                this.dateTimePickerDefault.startTime
                                                            }
                                                            defaultEndTime={
                                                                this.dateTimePickerDefault.endTime
                                                            }
                                                            onApplyRange={this.handleApplyRange}
                                                            onClear={this.handleClearDateRangePicker}
                                                        />
                                                    </div>
                                                )}
                                                <div className="time-period-menu">
                                                    {timeFrameOptions.map((option) => (
                                                        <button
                                                            key={option.startTime.valueOf()}
                                                            className="dropdown-item"
                                                            value={option.startTime.valueOf()}
                                                            onClick={this.handleTimeFrameChange}
                                                        >
                                                            {option.text}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="actions ml-auto">
                                            {this.state.isPrintable ? <i className="fas fa-lg fa-print" onClick={this.printDataReport} /> :
                                              <i className="fas fa-lg fa-print" style={{opacity: 0.6, cursor: "not-allowed"}}/>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div ref={this.neighborhoodDataContainer}>
                        <div className="row">
                            <div className="col-md-9">
                                <AlertCard auth={this.props.auth} history={this.props.history} rooms={rooms} alerts={alerts} startTime={this.state.startTime} isLoading={this.state.isLoadingAlerts} />
                                <div className="row neighborhood-alerts">
                                    <div className="col-md-6">
                                        <MovementCard movements={movements} isLoading={this.state.isLoadingMovement} />
                                    </div>
                                    <div className="col-md-6">
                                        <OutOfRangeCard outOfRangeCount={outOfRangeCount} timeFrameText={timeFrameText} isLoading={this.state.isLoadingOutOfRange}/>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="section-item-3">
                                    <SleepCard auth={this.props.auth} history={this.props.history} rooms={rooms} startTime={this.state.startTime} isLoading={this.state.isLoadingSleep} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default SectionData;
